/**
 * c27-journal-filters.js
 * Search / Filter / Sort for Journal pages.
 *
 * Modes:
 *  - Standalone browse/bloglist: toolbar + grid in one section
 *  - Landing page: toolbar-only controller filters all journal-row sections below
 */

(function () {
  'use strict';

  const rowInstances = [];

  function cloneState(s) {
    return {
      query:      s.query,
      categories: s.categories.slice(),
      readtimes:  s.readtimes.slice(),
      tags:       s.tags.slice(),
      sort:       s.sort,
    };
  }

  function isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  function isFilterActive(state) {
    return !!(
      state.query ||
      state.categories.length ||
      state.readtimes.length ||
      state.tags.length ||
      state.sort !== 'default'
    );
  }

  function matchesQuery(card, q) {
    if (!q) return true;
    return (card.dataset.title || '').toLowerCase().includes(q.toLowerCase());
  }

  function matchesCategory(card, cats) {
    if (!cats.length) return true;
    const cardTags = (card.dataset.tags || '').split(',').map(t => t.trim());
    return cats.some(c => {
      if (cardTags.includes(c)) return true;
      if (c === 'update' && cardTags.includes('updates')) return true;
      return false;
    });
  }

  function matchesReadtime(card, rts) {
    if (!rts.length) return true;
    const rt = parseInt(card.dataset.readtime, 10) || 1;
    return rts.some(bucket => {
      if (bucket === 'under2') return rt < 2;
      if (bucket === '2to5')  return rt >= 2 && rt <= 5;
      if (bucket === '5plus') return rt > 5;
      return false;
    });
  }

  function matchesTags(card, tags) {
    if (!tags.length) return true;
    const cardTags = (card.dataset.tags || '').split(',').map(t => t.trim());
    return tags.some(t => cardTags.includes(t));
  }

  function sortedCards(cards, sortKey, originalOrder) {
    const sorted = cards.slice();
    if (sortKey === 'newest') {
      sorted.sort((a, b) => parseInt(b.dataset.date, 10) - parseInt(a.dataset.date, 10));
    } else if (sortKey === 'oldest') {
      sorted.sort((a, b) => parseInt(a.dataset.date, 10) - parseInt(b.dataset.date, 10));
    } else if (sortKey === 'popular') {
      sorted.sort((a, b) => parseInt(b.dataset.comments, 10) - parseInt(a.dataset.comments, 10));
    } else {
      sorted.sort((a, b) => originalOrder.indexOf(a) - originalOrder.indexOf(b));
    }
    return sorted;
  }

  function rowMatchesCategoryFilter(sectionEl, categories) {
    if (!categories.length) return true;
    const rowCat = (sectionEl.dataset.cjbRowCategory || '').trim().toLowerCase();
    if (!rowCat) return false;
    return categories.some(c => c.trim().toLowerCase() === rowCat);
  }

  function applyStateToGrid(ctx, state, options) {
    const opts = options || {};
    const isGlobal = !!opts.isGlobal;
    const { grid, allCards, originalOrder, previewLimit, sectionEl, emptyEl } = ctx;

    if (isGlobal && state.categories.length) {
      if (!rowMatchesCategoryFilter(sectionEl, state.categories)) {
        sectionEl.hidden = true;
        return 0;
      }
      sectionEl.hidden = false;
    } else if (isGlobal) {
      sectionEl.hidden = false;
    }

    const cardState = isGlobal
      ? Object.assign({}, state, { categories: [] })
      : state;

    const sorted = sortedCards(allCards, cardState.sort, originalOrder);
    const active = isFilterActive(state);
    let visibleCount = 0;
    let previewShown = 0;

    sorted.forEach(card => {
      const matches =
        matchesQuery(card, cardState.query) &&
        matchesCategory(card, cardState.categories) &&
        matchesReadtime(card, cardState.readtimes) &&
        matchesTags(card, cardState.tags);

      let visible = matches;
      if (visible && !active && previewLimit > 0) {
        visible = previewShown < previewLimit;
        if (visible) previewShown++;
      }

      card.hidden = !visible;
      if (visible) visibleCount++;
    });

    sorted.forEach(card => grid.appendChild(card));

    if (sectionEl && !isGlobal) {
      sectionEl.hidden = false;
    }
    if (emptyEl) {
      emptyEl.hidden = visibleCount > 0;
    }

    return visibleCount;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Journal row (filter target) ───────────────────────────────── */
  function initRow(section) {
    const grid = section.querySelector('[data-cjb-grid]');
    if (!grid) return null;

    const ctx = {
      sectionEl: section,
      grid,
      allCards: Array.from(grid.querySelectorAll('.cjb-card-wrap')),
      originalOrder: [],
      previewLimit: parseInt(grid.dataset.cjbPreviewLimit, 10) || 0,
      emptyEl: section.querySelector('[data-cjb-empty]'),
    };
    ctx.originalOrder = ctx.allCards.slice();
    rowInstances.push(ctx);
    return ctx;
  }

  /* ── Toolbar controller (shared or standalone) ─────────────────── */
  function initToolbar(section, gridCtx) {
    const searchInput     = section.querySelector('[data-cjb-search]');
    const grid            = gridCtx ? gridCtx.grid : section.querySelector('[data-cjb-grid]');
    const activeRow       = section.querySelector('[data-cjb-active-row]');
    const chipsWrap       = section.querySelector('[data-cjb-chips]');
    const emptyEl         = gridCtx ? gridCtx.emptyEl : section.querySelector('[data-cjb-empty]');
    const isGlobal        = section.hasAttribute('data-cjb-toolbar-only') || !grid;

    const filtersTrigger  = section.querySelector('[data-cjb-filters-trigger]');
    const filtersOverlay  = section.querySelector('[data-cjb-filters-overlay]');
    const filtersPanel    = section.querySelector('[data-cjb-filters-panel]');
    const filtersCloseEls = section.querySelectorAll('[data-cjb-filters-close]');
    const fpApplyBtn      = section.querySelector('[data-cjb-fp-apply]');
    const fpClearBtn      = section.querySelector('[data-cjb-fp-clear]');
    const catFacet        = section.querySelector('[data-cjb-facet="category"]');
    const rtFacet         = section.querySelector('[data-cjb-facet="readtime"]');
    const tagFacet        = section.querySelector('[data-cjb-facet="tags"]');
    const sortTrigger     = section.querySelector('[data-cjb-sort-trigger]');
    const sortLabel       = section.querySelector('[data-cjb-sort-label]');
    const sortDropdown    = section.querySelector('[data-cjb-sort-dropdown]');
    const sortSheetOverlay = section.querySelector('[data-cjb-sort-sheet-overlay]');
    const sortSheetList   = section.querySelector('[data-cjb-sort-sheet-list]');
    const clearAllInline  = section.querySelector('[data-cjb-clear-all]');

    if (!isGlobal && !grid) return null;

    let standaloneCtx = null;
    if (!isGlobal && grid) {
      standaloneCtx = {
        sectionEl: section,
        grid,
        allCards: Array.from(grid.querySelectorAll('.cjb-card-wrap')),
        originalOrder: [],
        previewLimit: 0,
        emptyEl,
      };
      standaloneCtx.originalOrder = standaloneCtx.allCards.slice();
    }

    let state = {
      query: '',
      categories: [],
      readtimes: [],
      tags: [],
      sort: 'default',
    };
    let draft = cloneState(state);

    const initialCats = (section.dataset.cjbInitialCategories || '').trim().toLowerCase();
    if (initialCats) {
      state.categories = [initialCats];
      draft = cloneState(state);
    }

    function getTargets() {
      if (isGlobal) return rowInstances;
      return standaloneCtx ? [standaloneCtx] : [];
    }

    function applyFilters() {
      let totalVisible = 0;
      getTargets().forEach(ctx => {
        totalVisible += applyStateToGrid(ctx, state, { isGlobal });
      });
      updateChips();
      return totalVisible;
    }

    function getLabelForValue(facet, value) {
      const facetEl = facet === 'category' ? catFacet :
                      facet === 'readtime' ? rtFacet  : tagFacet;
      if (!facetEl) return value;
      const input = facetEl.querySelector(`input[value="${CSS.escape(value)}"]`);
      if (input) {
        const textEl = input.closest('.cjb-fp-option')?.querySelector('.cjb-fp-option-text');
        if (textEl) return textEl.textContent.trim();
      }
      return value;
    }

    function updateChips() {
      if (!chipsWrap || !activeRow) return;
      const items = [];
      state.categories.forEach(v => items.push({ facet: 'category', value: v, label: getLabelForValue('category', v) }));
      state.readtimes.forEach(v => items.push({ facet: 'readtime', value: v, label: getLabelForValue('readtime', v) }));
      state.tags.forEach(v => items.push({ facet: 'tags', value: v, label: getLabelForValue('tags', v) }));

      if (!items.length) {
        activeRow.hidden = true;
        chipsWrap.innerHTML = '';
        return;
      }

      activeRow.hidden = false;
      chipsWrap.innerHTML = '';
      items.forEach(({ facet, value, label }) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'cjb-chip';
        chip.innerHTML =
          `<span>${escapeHtml(label)}</span>` +
          `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">` +
          `<path d="M3 3l8 8M11 3L3 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` +
          `</svg>`;
        chip.addEventListener('click', () => removeFilter(facet, value));
        chipsWrap.appendChild(chip);
      });
    }

    function removeFilter(facet, value) {
      if (facet === 'category') state.categories = state.categories.filter(v => v !== value);
      if (facet === 'readtime') state.readtimes  = state.readtimes.filter(v => v !== value);
      if (facet === 'tags')     state.tags       = state.tags.filter(v => v !== value);
      draft = cloneState(state);
      syncCheckboxesFromState(draft);
      applyFilters();
    }

    function clearAllFilters() {
      state = { query: '', categories: [], readtimes: [], tags: [], sort: 'default' };
      if (searchInput) searchInput.value = '';
      if (sortLabel) sortLabel.textContent = sortDropdown?.querySelector('.cjb-sort-item[data-value="default"] span')?.textContent.trim() || 'Default';
      [sortDropdown, sortSheetList].forEach(container => {
        if (!container) return;
        container.querySelectorAll('.cjb-sort-item').forEach(item => {
          const selected = item.dataset.value === 'default';
          item.classList.toggle('cjb-sort-item--selected', selected);
          item.setAttribute('aria-selected', String(selected));
        });
      });
      draft = cloneState(state);
      syncCheckboxesFromState(draft);
      applyFilters();
    }

    function syncCheckboxesFromState(s) {
      if (catFacet) {
        catFacet.querySelectorAll('input[type="checkbox"]').forEach(inp => {
          const isCatAll = inp.hasAttribute('data-cjb-cat-all');
          if (isCatAll) {
            inp.checked = s.categories.length === 0;
            toggleCheckboxUI(inp, s.categories.length === 0);
          } else {
            const checked = s.categories.includes(inp.value);
            inp.checked = checked;
            toggleCheckboxUI(inp, checked);
          }
        });
      }
      if (rtFacet) {
        rtFacet.querySelectorAll('input[type="checkbox"]').forEach(inp => {
          const checked = s.readtimes.includes(inp.value);
          inp.checked = checked;
          toggleCheckboxUI(inp, checked);
        });
      }
      if (tagFacet) {
        tagFacet.querySelectorAll('input[type="checkbox"]').forEach(inp => {
          const checked = s.tags.includes(inp.value);
          inp.checked = checked;
          toggleCheckboxUI(inp, checked);
        });
      }
    }

    function toggleCheckboxUI(input, checked) {
      const box = input.closest('.cjb-fp-option')?.querySelector('[data-cjb-checkbox]');
      if (box) box.classList.toggle('cjb-checkbox--checked', checked);
    }

    function readCheckboxesToDraft() {
      draft.categories = [];
      draft.readtimes  = [];
      draft.tags       = [];
      if (catFacet) {
        catFacet.querySelectorAll('input[type="checkbox"]:checked').forEach(inp => {
          if (!inp.hasAttribute('data-cjb-cat-all') && inp.value) draft.categories.push(inp.value);
        });
      }
      if (rtFacet) {
        rtFacet.querySelectorAll('input[type="checkbox"]:checked').forEach(inp => {
          if (inp.value) draft.readtimes.push(inp.value);
        });
      }
      if (tagFacet) {
        tagFacet.querySelectorAll('input[type="checkbox"]:checked').forEach(inp => {
          if (inp.value) draft.tags.push(inp.value);
        });
      }
    }

    function setupCheckboxListeners(facetEl, isCat) {
      if (!facetEl) return;
      facetEl.querySelectorAll('.cjb-fp-option').forEach(optEl => {
        optEl.addEventListener('click', (e) => {
          e.preventDefault();
          const inp = optEl.querySelector('input[type="checkbox"]');
          if (!inp) return;
          if (isCat && inp.hasAttribute('data-cjb-cat-all')) {
            draft.categories = [];
            syncCheckboxesFromState(draft);
            return;
          }
          inp.checked = !inp.checked;
          toggleCheckboxUI(inp, inp.checked);
          if (isCat) {
            const allInp = catFacet.querySelector('[data-cjb-cat-all]');
            if (allInp) {
              allInp.checked = false;
              toggleCheckboxUI(allInp, false);
            }
          }
          readCheckboxesToDraft();
        });
      });
    }

    setupCheckboxListeners(catFacet, true);
    setupCheckboxListeners(rtFacet, false);
    setupCheckboxListeners(tagFacet, false);

    function openFilters() {
      draft = cloneState(state);
      syncCheckboxesFromState(draft);
      filtersOverlay.removeAttribute('aria-hidden');
      filtersOverlay.classList.add('cjb-filters-overlay--open');
      if (filtersTrigger) filtersTrigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeFilters() {
      filtersOverlay.setAttribute('aria-hidden', 'true');
      filtersOverlay.classList.remove('cjb-filters-overlay--open');
      if (filtersTrigger) filtersTrigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    if (filtersTrigger) filtersTrigger.addEventListener('click', openFilters);
    filtersCloseEls.forEach(el => el.addEventListener('click', closeFilters));
    if (filtersOverlay) {
      filtersOverlay.addEventListener('click', (e) => {
        if (e.target === filtersOverlay) closeFilters();
      });
    }
    if (fpApplyBtn) {
      fpApplyBtn.addEventListener('click', () => {
        readCheckboxesToDraft();
        state.categories = draft.categories.slice();
        state.readtimes  = draft.readtimes.slice();
        state.tags       = draft.tags.slice();
        applyFilters();
        closeFilters();
      });
    }
    if (fpClearBtn) {
      fpClearBtn.addEventListener('click', () => {
        draft = { query: draft.query, categories: [], readtimes: [], tags: [], sort: draft.sort };
        syncCheckboxesFromState(draft);
      });
    }

    function openSortDropdown() {
      sortDropdown.removeAttribute('aria-hidden');
      sortDropdown.classList.add('cjb-sort-dropdown--open');
      if (sortTrigger) sortTrigger.setAttribute('aria-expanded', 'true');
    }

    function closeSortDropdown() {
      sortDropdown.setAttribute('aria-hidden', 'true');
      sortDropdown.classList.remove('cjb-sort-dropdown--open');
      if (sortTrigger) sortTrigger.setAttribute('aria-expanded', 'false');
    }

    function openSortSheet() {
      sortSheetOverlay.removeAttribute('aria-hidden');
      sortSheetOverlay.classList.add('cjb-sort-sheet-overlay--open');
      if (sortTrigger) sortTrigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    function closeSortSheet() {
      sortSheetOverlay.setAttribute('aria-hidden', 'true');
      sortSheetOverlay.classList.remove('cjb-sort-sheet-overlay--open');
      if (sortTrigger) sortTrigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    if (sortTrigger) {
      sortTrigger.addEventListener('click', () => {
        if (isMobile()) {
          openSortSheet();
        } else if (sortDropdown.classList.contains('cjb-sort-dropdown--open')) {
          closeSortDropdown();
        } else {
          openSortDropdown();
        }
      });
    }

    function handleSortSelect(value, labelText) {
      state.sort = value;
      if (sortLabel) sortLabel.textContent = labelText;
      [sortDropdown, sortSheetList].forEach(container => {
        if (!container) return;
        container.querySelectorAll('.cjb-sort-item').forEach(item => {
          const selected = item.dataset.value === value;
          item.classList.toggle('cjb-sort-item--selected', selected);
          item.setAttribute('aria-selected', String(selected));
        });
      });
      applyFilters();
    }

    if (sortDropdown) {
      sortDropdown.querySelectorAll('.cjb-sort-item').forEach(item => {
        item.addEventListener('click', () => {
          handleSortSelect(item.dataset.value, item.querySelector('span')?.textContent.trim() || item.textContent.trim());
          closeSortDropdown();
        });
      });
    }
    if (sortSheetList) {
      sortSheetList.querySelectorAll('.cjb-sort-item').forEach(item => {
        item.addEventListener('click', () => {
          handleSortSelect(item.dataset.value, item.querySelector('span')?.textContent.trim() || item.textContent.trim());
          closeSortSheet();
        });
      });
    }

    document.addEventListener('click', (e) => {
      if (sortDropdown?.classList.contains('cjb-sort-dropdown--open')) {
        if (!sortDropdown.contains(e.target) && !sortTrigger?.contains(e.target)) {
          closeSortDropdown();
        }
      }
    });
    if (sortSheetOverlay) {
      sortSheetOverlay.addEventListener('click', (e) => {
        if (e.target === sortSheetOverlay) closeSortSheet();
      });
    }
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (filtersOverlay?.classList.contains('cjb-filters-overlay--open')) closeFilters();
      if (sortDropdown?.classList.contains('cjb-sort-dropdown--open')) closeSortDropdown();
      if (sortSheetOverlay?.classList.contains('cjb-sort-sheet-overlay--open')) closeSortSheet();
    });

    if (searchInput) {
      let debounceTimer;
      searchInput.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          state.query = searchInput.value.trim();
          applyFilters();
        }, 200);
      });
    }

    if (clearAllInline) clearAllInline.addEventListener('click', clearAllFilters);

    applyFilters();

    return { getState: () => cloneState(state), applyFilters };
  }

  function init() {
    document.querySelectorAll('[data-section-type="c27-journal-row"]').forEach(initRow);

    document.querySelectorAll('[data-section-type="c27-journal-browse"]').forEach(section => {
      initToolbar(section, null);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
